import { TextField } from "@project/shared/src/components/atom/TextField"
import { Button, notification } from "antd"
import { useFormik } from "formik"
import { useMutation } from "react-query"
import styled from "styled-components"
import * as yup from "yup"
import { createInquiry } from "../../../services"

const FormCardWrapper = styled.div`
  margin: auto;
  max-width: 620px;
  min-height: auto;
  border-radius: 40px;
  background: ${(props) => props.theme.historyCardGradient};
  padding: 32px;
  border: 1px solid #282f47;
  @media (max-width: 1023px) {
    max-width: 500px;
    padding: 20px 20px;
    border-radius: 20px;
  }
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`

const GradientButton = styled(Button)`
  margin-top: 20px;
  background: ${(props) => props.theme.highlightGradient};
  color: ${(props) => props.theme.base} !important;
  width: fit-content;
  padding: 10.5px 24px;
  border: none;
  border-radius: 8px;
  height: 50px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  span > img {
    margin-top: 8px;
    margin-left: 10px;
  }
  @media (max-width: 1023px) {
    span {
      font-size: 0.875rem;
      img {
        width: 17px;
      }
    }
    height: 38px;
  }
  @media (max-width: 600px) {
    width: 100%;
  }
`
const InputFieldWrapper = styled.div`
  & + & {
    margin-top: 20px;
  }
  & .text-area {
    width: 100%;
    .ant-input-affix-wrapper {
      background-color: #202534;
      color: ${(props) => props.theme.base};
      :hover {
        border: 1px solid ${(props) => props.theme.base};
      }
    }
    .ant-input {
      padding: 6px 16px;
      line-height: 38px;
      background-color: #202534;
      font-size: 1rem;
      @media (max-width: 500px) {
        font-size: 0.875rem;
      }
    }
  }
  & .text-field {
    width: 100%;
    .ant-input-affix-wrapper {
      background-color: #202534;
      color: ${(props) => props.theme.base};
      :hover {
        border: 1px solid ${(props) => props.theme.base};
      }
    }
    .ant-input {
      padding: 6px 16px;
      line-height: 38px;
      background-color: #202534;
      font-size: 1rem;
      font-family: "Inter", sans-serif;
      height: 50px;
      @media (max-width: 500px) {
        height: 38px;
        padding: 12px;
        font-size: 0.875rem;
      }
    }
  }
`

interface ContactType {
  full_name: string
  email: string
  phone_no: string
  inquiry: string
}

const ContactForm: React.FC = () => {
  const validationSchema = yup.object().shape({
    email: yup
      .string()
      .email("Please use valid email address")
      .required("Please enter email address"),
  })

  const notifyUser = (message: string) => {
    notification.success({
      key: "contact-form",
      message: message,
    })
  }

  const { mutate, isLoading } = useMutation(createInquiry, {
    onError: (error: any) => {
      const errorMsg = error?.data?.error?.error
      notifyUser(errorMsg || "Failed to submit inquiry")
    },
    onSuccess: () => {
      notifyUser("Inquiry submitted successfully")
      formik.resetForm()
    },
  })

  const formik = useFormik<ContactType>({
    initialValues: {
      full_name: "",
      email: "",
      phone_no: "",
      inquiry: "",
    },
    validationSchema,
    onSubmit: (values) => mutate(values),
  })

  return (
    <FormCardWrapper id="contact-us-form">
      <form onSubmit={formik.handleSubmit}>
        <InputFieldWrapper>
          <TextField
            label="Full Name"
            name={"full_name"}
            className={"text-field"}
            borderradius={"6px"}
            value={formik.values.full_name}
            onChange={formik.handleChange}
            placeholder={"Your full name"}
            bordercolor="#313952"
            placeholdercolor="#FFFFFF99"
          />
        </InputFieldWrapper>

        <InputFieldWrapper>
          <TextField
            label="Email address"
            name={"email"}
            className={"text-field"}
            borderradius={"6px"}
            onBlur={formik.handleBlur}
            value={formik.values.email}
            error={formik.touched.email && formik.errors.email}
            onChange={formik.handleChange}
            placeholder={"Your email address"}
            bordercolor="#313952"
            placeholdercolor="#FFFFFF99"
          />
        </InputFieldWrapper>

        <InputFieldWrapper>
          <TextField
            label="Phone Number"
            name={"phone_no"}
            className={"text-field"}
            borderradius={"6px"}
            value={formik.values.phone_no}
            placeholder={"Your Phone number"}
            onChange={formik.handleChange}
            bordercolor="#313952"
            placeholdercolor="#FFFFFF99"
          />
        </InputFieldWrapper>

        <InputFieldWrapper>
          <TextField
            label="Tell us more about your concern"
            name={"inquiry"}
            type={"textarea"}
            className={"text-area"}
            value={formik.values.inquiry}
            borderradius={"6px"}
            placeholder={"Write your inquiry here"}
            onChange={formik.handleChange}
            rows={4.158}
            bordercolor="#313952"
            placeholdercolor="#FFFFFF99"
          />
        </InputFieldWrapper>
        <ButtonWrapper>
          <GradientButton loading={isLoading} htmlType={"submit"}>
            {"SEND INQUIRY"}
            <span>
              <img src={"assets/icons/arrow.svg"} />
            </span>
          </GradientButton>
        </ButtonWrapper>
      </form>
    </FormCardWrapper>
  )
}

export { ContactForm }
